import { Link } from 'react-router'
import { BackLink } from '#app/components/back-link.tsx'
import { PageFrame, ReadingColumn } from '#app/components/page-frame.tsx'
import { DemoCapsule } from '#app/features/portfolio/components/demo-capsule.tsx'
import { HarnessDiagram } from '#app/features/portfolio/components/harness-diagram.tsx'
import { type Route } from './+types/writing.harness.ts'

export const meta: Route.MetaFunction = () => [
	{
		title:
			'Why the model is for reasoning and code is for computation · slvr.mn',
	},
	{
		name: 'description',
		content:
			'The architecture of an agentic harness: the model proposes, a deterministic engine computes, and a failed proposal is rebuilt rather than patched.',
	},
]

export default function HarnessPage() {
	return (
		<PageFrame>
			<ReadingColumn>
				<BackLink fallback={{ to: '/writing', label: 'Writing' }} />
				<h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
					Why the model is for reasoning and code is for computation
				</h1>
				<DemoCapsule href="/writing/harness" />
				<p className="text-muted-foreground mt-4 text-pretty">
					Most people use AI models like search engines, when they are closer to
					question engines. The strength is in writing the ranking algorithm or
					the search query, not in finding the top-ranked result. A model can
					recognise a CSV export, a screenshot and an API response as the same
					transfer — a judgment call. It cannot be relied on to sum the numbers.
					Code absolutely can. So the model proposes inputs, and a deterministic
					engine regenerates the books from those inputs plus rules.
				</p>

				<HarnessDiagram />

				<h2 className="mt-10 text-xl font-semibold tracking-tight">
					Why you cannot just change one number
				</h2>
				<p className="text-muted-foreground mt-3 text-pretty">
					Double-entry and FIFO cost basis are path-dependent. Change one early
					acquisition — its price, its date, whether it was a disposal at all —
					and every later disposal that drew from that lot is wrong too. There
					is no correcting a figure in place; the chronology has to be re-run
					from where the change lands. That is where deterministic books stop
					being a preference and become the only thing that works.
				</p>

				<p className="text-muted-foreground mt-6 text-sm">
					This is one kind of harness. What else a model can be pointed at —
					code, a shipped surface, a session —{' '}
					<Link
						to="/writing/types-of-agentic-harness"
						className="text-foreground underline underline-offset-4"
					>
						is a separate question
					</Link>
					.
				</p>
			</ReadingColumn>
		</PageFrame>
	)
}
